/**
 * Package Provider
 * Handles \usepackage{...} completions
 */

import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { PositionInfo, LaTeXContext } from '../types'
import { CompletionProvider } from './baseProvider'
import { logDebug } from '@utils/logger'

// Common LaTeX packages
const COMMON_PACKAGES = [
  'amsmath', 'amsfonts', 'amssymb', 'amsthm', 'graphicx', 'float', 'subcaption',
  'geometry', 'listings', 'xcolor', 'hyperref', 'biblatex', 'natbib', 'tikz',
  'pgfplots', 'algorithm', 'algorithmic', 'booktabs', 'array', 'multirow',
  'siunitx', 'cleveref', 'fancyhdr', 'titlesec', 'enumitem', 'parskip',
  'microtype', 'fontenc', 'inputenc', 'babel', 'csquotes', 'url', 'verbatim'
]

export class PackageProvider implements CompletionProvider {
  name = 'package'
  priority = 80

  canHandle(context: CompletionContext, position: PositionInfo, latexContext: LaTeXContext): boolean {
    // Check if we're in \usepackage{...}
    return position.isInCommand && 
           position.commandName === 'usepackage' &&
           position.isInBraces
  }

  provide(context: CompletionContext, position: PositionInfo, latexContext: LaTeXContext): CompletionResult | null {
    const { state, pos } = context
    const text = state.doc.toString()
    
    // Find \usepackage{ position
    let usepackagePos = -1
    let packageStart = pos
    
    for (let i = pos - 1; i >= 0 && (pos - i) <= 100; i--) {
      if (text.substring(i - 10, i + 1) === '\\usepackage{') {
        usepackagePos = i - 10
        packageStart = i + 1
        break
      }
    }
    
    if (usepackagePos < 0) return null
    
    const query = text.substring(packageStart, pos).toLowerCase()
    
    // Filter packages matching query
    const matches = COMMON_PACKAGES
      .filter(pkg => pkg.toLowerCase().startsWith(query) || pkg.toLowerCase().includes(query))
      .slice(0, 30)
    
    if (matches.length === 0) return null
    
    const options = matches.map(pkg => ({
      label: pkg,
      type: 'text' as const,
      info: `LaTeX package: ${pkg}`,
      apply: pkg
    }))
    
    logDebug('[PackageProvider] Providing completions', {
      query,
      optionsCount: options.length
    })
    
    return {
      from: packageStart,
      to: pos,
      options,
      filter: false,
      span: /^[a-zA-Z0-9-]*$/,
      validFor: /^[a-zA-Z0-9-]*$/
    } as CompletionResult & { span: RegExp; validFor: RegExp }
  }
}

